import { SelectHTMLAttributes, useEffect, useRef, useState } from 'react';
import { values } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useOrgs } from 'hooks/useOrgs/useOrgs';
import { OrgId } from 'shared/models';

require('./OrgSelectField.css');

export type Props = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value'> & {
    id: string;
    label?: string;
    labelKey?: string;
    orgsToExclude?: OrgId[];
    emptyOptionTextKey?: string;
    disableSsoOrg?: boolean;
    value: OrgId | null;
};

export const OrgSelectField = ({ id, required, label, labelKey, className, orgsToExclude, value, emptyOptionTextKey, disableSsoOrg, ...otherProps }: Props) => {
    const { t } = useTranslation();
    
    const selectElementRef = useRef<HTMLSelectElement | null>(null);
    const [ initialized, setInitialized ] = useState(false);

    const { orgs, errorMessage } = useOrgs();

    useEffect(() => {
        // Update the Semantic UI org selection drop down with the new value
        const selectElement = selectElementRef.current;
        if (selectElement && orgs && values(orgs).length && !initialized) {
            setInitialized(true);
        }
    }, [ selectElementRef, orgs, value, initialized, setInitialized ]);

    return (
        <div
            className={ `org-select-field ${required ? 'required field' : 'field'}` }
        >
            { (label || labelKey) &&
                <label htmlFor={ id }>
                    { label || t(labelKey!) }
                </label>
            }
            <select
                className={ `${className || ''} ui fluid dropdown` }
                id={ id }
                { ...otherProps }
                ref={ selectElementRef }
                value={ value || '' }
            >
                <option value="">
                    { t(emptyOptionTextKey || 'NONE') }
                </option>
                { orgs && values(orgs)
                    .filter(org => !orgsToExclude || orgsToExclude.indexOf(org.id) === -1)
                    .sort((a, b) => a.displayName.localeCompare(b.displayName))
                    .map(org => {
                        return (
                            <option
                                key={ org.id }
                                value={ org.id }
                                disabled={ disableSsoOrg && !!org.ssoProvider }
                            >
                                { org.displayName }
                            </option>
                        );
                    })}
            </select>
            { errorMessage &&
                <div className="error">
                    { errorMessage }
                </div>
            }
        </div>
    );
};
